/**
 * User 권한 관리 유틸리티
 *
 * 사용자의 역할(role)에 따른 권한을 정의하고 검증하는 함수들을 제공합니다.
 *
 * @fix BUG-009: 권한 관리 미구현 해결
 */

import type { User, UserRole } from '../types/User'

/**
 * 시스템 권한 타입
 *
 * - user.update_role: 사용자 역할 변경 권한
 * - user.remove: 사용자 제거 권한
 * - settings.update: 워크스페이스 설정 변경 권한
 * - category.manage: 카테고리 관리 권한
 * - budget.create: 예산 생성 권한
 */
export type Permission =
  | 'user.update_role'
  | 'user.remove'
  | 'settings.update'
  | 'category.manage'
  | 'budget.create'

/**
 * 역할별 권한 매핑
 *
 * Owner: 모든 관리 권한 보유
 * Member: 기본 권한 없음 (자신의 데이터만 수정 가능)
 */
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  owner: [
    'user.update_role',
    'user.remove',
    'settings.update',
    'category.manage',
    'budget.create',
  ],
  member: [],
}

/**
 * 사용자가 특정 권한을 가지고 있는지 확인
 *
 * @param user - 권한을 확인할 사용자
 * @param permission - 확인할 권한
 * @returns true: 권한 있음, false: 권한 없음
 *
 * @example
 * ```ts
 * const canManageUsers = hasPermission(currentUser, 'user.update_role')
 * if (!canManageUsers) {
 *   alert('권한이 없습니다')
 * }
 * ```
 */
export function hasPermission(user: User, permission: Permission): boolean {
  const permissions = ROLE_PERMISSIONS[user.role]
  return permissions.includes(permission)
}

/**
 * 사용자가 다른 사용자의 역할을 변경할 수 있는지 확인
 *
 * 검증 규칙:
 * 1. 실행자(actor)가 'user.update_role' 권한을 가지고 있어야 함 (Owner만 가능)
 * 2. 실행자가 자신의 Owner 권한을 낮추는 것은 불가
 *
 * @param actor - 역할 변경을 시도하는 사용자 (현재 로그인 유저)
 * @param target - 역할이 변경될 대상 사용자
 * @param newRole - 변경하려는 새 역할 (선택적)
 * @returns true: 변경 가능, false: 변경 불가
 *
 * @example
 * ```ts
 * if (!canChangeUserRole(currentUser, targetUser, 'member')) {
 *   alert('권한이 없습니다')
 *   return
 * }
 * await updateUserRole(targetUser.id, 'member')
 * ```
 */
export function canChangeUserRole(
  actor: User,
  target: User,
  newRole?: UserRole
): boolean {
  // 1. 역할 변경 권한이 있는지 확인
  if (!hasPermission(actor, 'user.update_role')) {
    return false
  }

  // 2. 자신의 Owner 권한을 낮추는 경우 차단
  if (newRole && isDemotingSelf(actor, target, newRole)) {
    return false
  }

  return true
}

/**
 * 사용자가 다른 사용자를 제거할 수 있는지 확인
 *
 * 검증 규칙:
 * 1. 실행자(actor)가 'user.remove' 권한을 가지고 있어야 함 (Owner만 가능)
 * 2. Owner는 제거 불가 (member만 제거 가능)
 *
 * @param actor - 제거를 시도하는 사용자 (현재 로그인 유저)
 * @param target - 제거될 대상 사용자
 * @returns true: 제거 가능, false: 제거 불가
 *
 * @example
 * ```ts
 * if (!canRemoveUser(currentUser, targetUser)) {
 *   alert('권한이 없습니다')
 *   return
 * }
 * await removeUser(targetUser.id)
 * ```
 */
export function canRemoveUser(actor: User, target: User): boolean {
  // 1. 제거 권한이 있는지 확인
  if (!hasPermission(actor, 'user.remove')) {
    return false
  }

  // 2. Owner는 제거 불가
  if (target.role === 'owner') {
    return false
  }

  return true
}

/**
 * 실행자가 자신의 Owner 권한을 낮추려는 시도인지 확인
 *
 * @param actor - 실행자 (현재 로그인 유저)
 * @param target - 대상 사용자
 * @param newRole - 변경하려는 새 역할
 * @returns true: 자신의 권한을 낮추는 시도, false: 아님
 *
 * @why Owner가 실수로 자신의 권한을 낮추는 것을 방지하기 위한 안전장치
 */
export function isDemotingSelf(
  actor: User,
  target: User,
  newRole: UserRole
): boolean {
  return (
    actor.id === target.id && actor.role === 'owner' && newRole === 'member'
  )
}

/**
 * 워크스페이스에 최소 1명 이상의 Owner가 유지되는지 확인
 *
 * @param allUsers - 전체 사용자 목록
 * @param target - 제거 또는 강등될 대상 사용자
 * @param action - 수행할 작업 ('remove' | 'demote')
 * @returns true: 최소 Owner 수 유지됨, false: 위반
 *
 * @why 워크스페이스에 최소 1명의 Owner는 반드시 존재해야 관리가 가능합니다.
 *      마지막 Owner를 제거하거나 강등하면 워크스페이스 관리 불가 상태가 됩니다.
 *
 * @example
 * ```ts
 * if (!checkMinimumOwners(allUsers, targetUser, 'demote')) {
 *   alert('최소 1명의 Owner가 필요합니다')
 *   return
 * }
 * await updateUserRole(targetUser.id, 'member')
 * ```
 */
export function checkMinimumOwners(
  allUsers: User[],
  target: User,
  action: 'remove' | 'demote'
): boolean {
  // 활성 상태의 Owner 수 계산
  const activeOwners = allUsers.filter(
    (u) => u.isActive && u.role === 'owner'
  )

  // 제거 또는 강등 대상이 Owner인 경우
  if ((action === 'remove' || action === 'demote') && target.role === 'owner') {
    // 최소 2명 이상의 Owner가 있어야 1명을 제거/강등 가능
    return activeOwners.length > 1
  }

  return true
}
